'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, InfoIcon } from '@nwwp/ui-shadcn'
import { getOtpStatus, sendOtpMessage } from '~/actions/otp'
import { PhoneDisclosureBody } from '~/components/LeadMagnet/PhoneDisclosure'
import { funnelSchema } from '~/lib/schemas/funnel-schema'
import { useFunnelStore } from '../../_hooks/use-funnel-store'
import type { FunnelStepComponent } from '../../types'
import { FunnelStep } from '../FunnelStep'

const schema = funnelSchema.pick({ phone: true })
type Schema = z.infer<typeof schema>

export const Phone: FunnelStepComponent = ({ onSubmit }) => {
  const setData = useFunnelStore.getState().setData
  const phone = useFunnelStore.getState().data.phone
  const [isLoading, setIsLoading] = useState(false)
  const { control, formState, handleSubmit } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { phone: phone ? phone.replace('+1', '') : '' },
    mode: 'onChange',
  })

  const handleSubmitPhone = async (data: Schema) => {
    setIsLoading(true)
    try {
      const isPhoneAlreadyVerified = await getOtpStatus(data.phone)

      if (isPhoneAlreadyVerified) {
        setData({ verified_otp: true })
        onSubmit(data)
        return
      }

      setData({ verified_otp: false })
      await sendOtpMessage(data.phone)
      onSubmit(data)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <FunnelStep onSubmit={handleSubmit(handleSubmitPhone)}>
      <div className="flex flex-col gap-1">
        <FunnelStep.Title>Please provide your contact phone number</FunnelStep.Title>
        <div className="flex items-start gap-1 tablet:items-center">
          <InfoIcon className="size-6 shrink-0 text-green-lighter" aria-hidden="true" />
          <p className="[font-size:16px] [line-height:24px] font-medium text-white">
            Keep an eye out for text confirmation
          </p>
        </div>
      </div>
      <FunnelStep.NumberInput
        control={control}
        name="phone"
        disabled={isLoading}
        placeholder="(888) 888-8888"
        pattern="(###) ###-####"
        autoFocus
      />
      <Accordion
        type="single"
        collapsible
        className="overflow-hidden rounded-2xl border border-divider bg-neutral-surface text-neutral-light"
      >
        <AccordionItem value="disclosure" className="border-0">
          <AccordionTrigger className="px-4 font-sans [font-size:13px] [line-height:19.5px] font-normal text-neutral-light">
            By clicking &ldquo;Submit&rdquo; below, I agree to the Terms and Disclosures and Consent to Marketing Calls
            and Texts.
          </AccordionTrigger>
          <AccordionContent className="px-4 [font-size:13px] [line-height:19.5px] font-normal text-neutral-light">
            <PhoneDisclosureBody />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <FunnelStep.Button disabled={!formState.isValid}>Submit</FunnelStep.Button>
    </FunnelStep>
  )
}

Phone.path = 'phone'
Phone.schema = schema
